import React, { createElement } from 'react';
import { Redirect, Route, Switch, withRouter } from 'react-router-dom';
import { compose } from 'recompose';
import queryString from 'query-string';

import { unpackSessionObject } from './unpackSessionObject';
import SuperAdministratorHomepage from './user/super-admin/Homepage';
import MonitorEngineerHomepage from './user/monitoring/Homepage';
import AdministratorHomepage from './user/admin/Homepage';
import FrontDeskHomepage from './user/front/Homepage';
import SOAdminHomepage from './user/surgeon/admin/Homepage';
import SONursingHomepage from './user/surgeon/nursing/Homepage';
import NursingHomepage from './user/nursing/Homepage';
import ManagerHomepage from './user/businessManager/Homepage';
import SchedulerHomepage from './user/scheduler/Homepage';
import PreOPNurseHomepage from './user/preop-nurse/Homepage';
import SensorSupervisorHomepage from './user/sensor-supervisor/Homepage';
import OperationManagerHomepage from './user/operationManager/Homepage';
import PhysicianHomepage from './user/physician/Homepage';
import AnesthesiologistHomepage from './user/anesthesiologist/Homepage';
import StaffMemberHomepage from './user/staff-member/Homepage';
import LiaisonHomepage from './user/liaison/Homepage';
import SurgeonOfficeStaffHomepage from './user/surgeonOfficeStaff/Homepage';
import NoPermissions from './user/NoPermissions';
import Login from './user/login/Login';
import ReleaseNotesPage from './release-notes/ReleaseNotesPage';
import get from 'lodash/get';

import {
  ADMIN,
  BUSINESS_MANAGER,
  FRONT_DESK,
  GROUP_ADMIN,
  MONITORING,
  NURSE,
  NURSING,
  OPERATIONS_MANAGER,
  PREOP_NURSE,
  SCHEDULER,
  SENSOR_SUPERVISOR,
  SUPER_ADMIN,
  PHYSICIAN,
  ANESTHESIOLOGIST,
  STAFF_MEMBER,
  LIAISON,
  SURGEONOFFICESTAFF,
} from '../entities/role/enums';
import { withSession } from '@/state/Session';
import ExpireableConnectionProvider from '../../components/ExpireableConnectionProvider';
import { OrganizationType } from '../entities/hospitals/enums';
import Initialization from './user/Initialization';
import ResetPasswordPage from './user/ResetPasswordPage';
import SetPasswordPage from './user/SetPasswordPage';
import { Scope, ScopeProvider } from '@/contexts/ScopeContext';
import Button from '@/se/components/Button[DEPRECATED]';

/**
 * This component expect session to be either false or an object having a boolean valid value.
 *
 * Until we get to the async rendering, parent component should wait for session to be ready.
 */

const SCRoleHomepages = {
  [SUPER_ADMIN]: SuperAdministratorHomepage,
  [GROUP_ADMIN]: SuperAdministratorHomepage,
  [MONITORING]: MonitorEngineerHomepage,
  [ADMIN]: AdministratorHomepage,
  [FRONT_DESK]: FrontDeskHomepage,
  [SENSOR_SUPERVISOR]: SensorSupervisorHomepage,
  [NURSING]: NursingHomepage,
  [OPERATIONS_MANAGER]: OperationManagerHomepage,
  [BUSINESS_MANAGER]: ManagerHomepage,
  [PREOP_NURSE]: PreOPNurseHomepage,
  [SCHEDULER]: SchedulerHomepage,
  [PHYSICIAN]: PhysicianHomepage,
  [ANESTHESIOLOGIST]: AnesthesiologistHomepage,
  [STAFF_MEMBER]: StaffMemberHomepage,
  [LIAISON]: LiaisonHomepage,
  [SURGEONOFFICESTAFF]: SurgeonOfficeStaffHomepage,
};

const SORoleHomepages = {
  [ADMIN]: SOAdminHomepage,
  [NURSE]: SONursingHomepage,
};

const HomepageSwitch = ({ role, scope, ...rest }) => {
  if (scope === undefined) {
    // Loading organization scope data
    return <Initialization />;
  }

  const routesForType = () => {
    if (get(scope, 'hospital.type') === OrganizationType.SurgeonOffice) {
      return SORoleHomepages[role];
    } else {
      return SCRoleHomepages[role];
    }
  };
  const Homepage = routesForType() || NoPermissions;

  return <Homepage {...rest} />;
};

const User = ({ location, session, role }) => {
  const loggedIn = session && session.valid;

  const nextQueryString = () =>
    queryString.stringify({
      ...queryString.parse(location.search),
      next: location.pathname,
    });

  return (
    <Switch>
      <Route exact path={`/login`} component={Login} />
      <Route exact path={`/set-password`} component={SetPasswordPage} />
      <Route exact path={`/reset-password`} component={ResetPasswordPage} />
      <Route
        path={`/release-notes`}
        render={props =>
          loggedIn ? (
            <ExpireableConnectionProvider session={session}>
              <ReleaseNotesPage {...props} />
            </ExpireableConnectionProvider>
          ) : (
            <Redirect to={`/login?${nextQueryString()}`} />
          )
        }
      />
      <Route
        render={props =>
          loggedIn ? (
            <ExpireableConnectionProvider session={session}>
              <ScopeProvider>
                <Scope.Consumer>{scope => <HomepageSwitch role={role} scope={scope} {...props} />}</Scope.Consumer>
              </ScopeProvider>
            </ExpireableConnectionProvider>
          ) : (
            <Redirect to={`/login?${nextQueryString()}`} />
          )
        }
      />
    </Switch>
  );
};

const forceChooseMembership = Component => props =>
  createElement(props.memberships ? FullScreenMembershipPicker : Component, props);

export default compose(withRouter, withSession(unpackSessionObject), forceChooseMembership)(User);

const FullScreenMembershipPicker = ({ memberships, selectMembership }) => (
  <div
    style={{
      minWidth: '100vw',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'start',
        justifyContent: 'center',
        gap: '1em',
      }}
    >
      <strong style={{ textAlign: 'start' }}>Select Surgery Center</strong>
      <ul
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'start',
          justifyContent: 'center',
          gap: '1em',
          margin: 0,
          padding: 0,
        }}
      >
        {memberships.map(membership => (
          <li key={membership.id}>
            <Button onClick={() => selectMembership(membership.id)}>{membership.organization.name}</Button>
          </li>
        ))}
      </ul>
    </div>
  </div>
);
